import React, { useEffect, useState } from "react";
import api from "../../../services/axios";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import {
  MdLockReset,
  MdMailOutline,
  MdLockOutline,
  MdVisibility,
  MdVisibilityOff,
  MdErrorOutline,
} from "react-icons/md";

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-700 dark:border-white/10 dark:bg-navy-900 dark:text-white dark:disabled:bg-navy-900/60";

const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);

  useEffect(() => {
    const userEmail = localStorage.getItem("userEmail");
    setEmail(userEmail);
  }, [email]);

  function togglePasswordVisibility() {
    setIsPasswordVisible((prevState) => !prevState);
  }

  const handleForgotPassword = async () => {
    if (email !== null && currentPassword !== null && newPassword !== null) {
      try {
        setLoading(true);
        await api.post("/Auth/Forgot-Password", {
          email,
          currentPassword,
          newPassword,
        });

        navigate("/admin/profile");
        toast.success("Senha redefinida com sucesso!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } catch (error) {
        setErrorMessage(
          "Não foi possível redefinir a senha. Verifique seus dados e tente novamente."
        );
        setLoading(false);
        toast.error("Ops! Algo deu errado.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
    }
  };

  const isDisabled =
    email.length < 5 || currentPassword.length < 5 || newPassword.length < 5;

  return (
    <div className="flex min-h-dvh w-full items-center justify-center px-4 py-8">
      <form
        onSubmit={(e) => e.preventDefault()}
        className="animate-fade-up w-full max-w-md"
      >
        <div className="relative flex flex-col overflow-hidden rounded-[20px] border border-gray-100 bg-white bg-clip-border p-6 shadow-dv-lg dark:border-white/10 dark:!bg-navy-800 dark:text-white sm:p-8">
          <div
            className="pointer-events-none absolute -right-16 -top-20 h-44 w-44 rounded-full bg-dv-blue-500/10 blur-3xl dark:bg-dv-blue-500/15"
            aria-hidden="true"
          />
          {/* Header */}
          <div className="relative mb-6 flex flex-col items-center text-center">
            <div className="dv-aura mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow">
              <MdLockReset className="h-7 w-7" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight text-navy-700 dark:text-white">
              Redefinir senha
            </h1>
            <p className="mt-1.5 text-sm text-gray-700 dark:text-gray-300">
              Informe sua senha atual e escolha uma nova senha
            </p>
          </div>

          {errorMessage && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300"
            >
              <MdErrorOutline
                className="mt-0.5 h-4 w-4 shrink-0"
                aria-hidden="true"
              />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Email */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="email"
              className="text-sm font-semibold text-gray-700 dark:text-gray-300"
            >
              E-mail
            </label>
            <div className="relative">
              <MdMailOutline
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 dark:text-gray-400"
                aria-hidden="true"
              />
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`${inputCls} pl-9`}
                placeholder="seu@email.com"
                required
                disabled={email !== null}
              />
            </div>
          </div>

          {/* Current password */}
          <div className="mt-4 flex flex-col gap-1.5">
            <label
              htmlFor="currentPassword"
              className="text-sm font-semibold text-gray-700 dark:text-gray-300"
            >
              Senha atual <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MdLockOutline
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 dark:text-gray-400"
                aria-hidden="true"
              />
              <input
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className={`${inputCls} pl-9`}
                placeholder="Digite sua senha atual"
                type={isPasswordVisible ? "text" : "password"}
                autoComplete="current-password"
              />
            </div>
          </div>

          {/* New password */}
          <div className="mt-4 flex flex-col gap-1.5">
            <label
              htmlFor="newPassword"
              className="text-sm font-semibold text-gray-700 dark:text-gray-300"
            >
              Nova senha <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MdLockOutline
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 dark:text-gray-400"
                aria-hidden="true"
              />
              <input
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={`${inputCls} px-9`}
                placeholder="Digite a nova senha"
                type={isPasswordVisible ? "text" : "password"}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                aria-label={isPasswordVisible ? "Ocultar senhas" : "Mostrar senhas"}
                aria-pressed={isPasswordVisible}
                className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-dv-blue-600 dark:text-gray-400 dark:hover:bg-navy-700 dark:hover:text-dv-cyan-300"
              >
                {isPasswordVisible ? (
                  <MdVisibilityOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <MdVisibility className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="relative mt-6 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => navigate("/admin/profile")}
              className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
            >
              Cancelar
            </button>
            <button
              type="submit"
              onClick={handleForgotPassword}
              disabled={loading || isDisabled}
              className="dv-sheen flex items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <>
                  <span
                    className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                    aria-hidden="true"
                  />
                  Processando…
                </>
              ) : (
                "Redefinir senha"
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ResetPassword;
